import { showAppAlert } from '../common/appDialog';
import React, { useState, useEffect } from 'react';
import { db } from '../../firebaseConfig';
import { 
    collection, 
    query, 
    where, 
    onSnapshot,
    orderBy,
    setDoc,
    doc,
    updateDoc
} from 'firebase/firestore';
import type { SrmProject, Organization, Page, NavParams, RiskEntry, SrmModule, SrmAlert, ActiveFramework } from '@types';
import { getOrganizationDisplayName } from '@common/organizationDisplayName';

import SrmProjectDashboard from './SrmProjectDashboard';
import SrmArchitectInput from './SrmArchitectInput';
import { extractRisksFromIntelligence } from '../utils/srmRisks';
import SrmProjectsList from './SrmProjectsList';
import Loader from './Loader';
import { PlusIcon, ChevronLeftIcon, Squares2X2Icon, BellAlertIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface SrmCommandCenterProps {
    organization: Organization | null;
    onNavigate: (nav: { page: Page; params?: NavParams }) => void;
    projectId?: string; // Unified from initialProjectId
    initialView?: SrmView;
    activeClientId?: string;
    activeClientName?: string;
    onDeploy?: (framework: ActiveFramework) => void;
    navParams?: NavParams;
    readOnly?: boolean;
}

type SrmView = 'list' | 'architect' | 'dashboard';

const SrmCommandCenter: React.FC<SrmCommandCenterProps> = ({ 
    organization, 
    onNavigate, 
    projectId, 
    initialView, 
    activeClientId, 
    activeClientName, 
    onDeploy,
    navParams,
    readOnly = false
}) => {
    const [projects, setProjects] = useState<SrmProject[]>([]);
    const [selectedProject, setSelectedProject] = useState<SrmProject | null>(null);
    const [activeView, setActiveView] = useState<SrmView>(initialView || 'list');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // ── Live Critical Alert Banner State ──────────────────────────────────────
    interface CriticalAlertSummary { projectId: string; projectName: string; alertCount: number; }
    const [criticalAlertBanner, setCriticalAlertBanner] = useState<CriticalAlertSummary | null>(null);
    const [bannerDismissed, setBannerDismissed] = useState(false);

    // 1. Initialize view state based on props
    useEffect(() => {
        if (projectId) {
            setActiveView('dashboard');
        } else if (initialView) {
            setActiveView(initialView);
        } else {
            setActiveView('list');
        }
    }, [projectId, initialView]);

    // 2. Fetch All SRM Projects for the Organization
    useEffect(() => {
        if (!db || !organization) return;

        const q = query(
            collection(db, 'srm_projects'),
            where('organizationId', '==', organization.id),
            orderBy('createdAt', 'desc')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const fetched = snapshot.docs.map(doc => ({
                ...doc.data(),
                id: doc.id
            } as SrmProject));
            
            setProjects(fetched);
            setIsLoading(false);

            // Sync selected project if it changed in Firestore
            if (selectedProject) {
                const updated = fetched.find(p => p.id === selectedProject.id);
                if (updated) {
                    // If the project was just archived, navigate cleanly back to the list
                    if ((updated.status as string) === 'Archived') {
                        setSelectedProject(null);
                        setActiveView('list');
                    } else {
                        setSelectedProject(updated);
                    }
                } else {
                    // Project no longer exists in Firestore (permanently deleted)
                    setSelectedProject(null);
                    setActiveView('list');
                }
            }

            // Handle initial project selection
            if (projectId && !selectedProject) {
                const match = fetched.find(p => p.id === projectId || p.projectNumber === projectId);
                if (match) setSelectedProject(match);
            }
        }, (err) => {
            console.error("Error fetching projects:", err);
            setError("Failed to sync projects.");
            setIsLoading(false);
        });

        return () => unsubscribe();
    }, [organization?.id, projectId]);

    // ── Derive Critical Alert Banner from live projects ───────────────────────
    useEffect(() => {
        setBannerDismissed(false); // Reset on project change
        const projectsWithCritical = projects.filter(p =>
            (p.alerts || []).some((a: SrmAlert) => a.level === 'Critical' && a.status === 'Active')
        );
        if (projectsWithCritical.length > 0) {
            const first = projectsWithCritical[0];
            const criticalCount = projectsWithCritical.reduce(
                (acc, p) => acc + (p.alerts || []).filter((a: SrmAlert) => a.level === 'Critical' && a.status === 'Active').length, 0
            );
            setCriticalAlertBanner({
                projectId: first.id,
                projectName: first.name || first.projectName || 'Project',
                alertCount: criticalCount
            });
        } else {
            setCriticalAlertBanner(null);
        }
    }, [projects]);

    const handleSwitchProject = (id: string) => {
        const project = projects.find(p => p.id === id);
        if (project) {
            setSelectedProject(project);
            setActiveView('dashboard');
        }
    };

    const handleSaveSrmProject = async (project: SrmProject) => {
        if (!db) return;
        try {
            await setDoc(doc(db, 'srm_projects', project.id), project);
            setSelectedProject(project);
            setActiveView('dashboard');
        } catch (err) {
            console.error("Error saving project:", err);
            void showAppAlert("Failed to save project.");
        }
    };

    const handleUpdateSrmProjectModule = async (projectId: string, moduleId: string, resultData: any) => {
        if (!db || !selectedProject) return;

        try {
            // 1. Extract new risk CANDIDATES from the intelligence result
            const candidates = extractRisksFromIntelligence(moduleId, resultData);

            // 2. Deduplicate against BOTH existing active risks AND already-pending risks
            const existingRisks: RiskEntry[] = selectedProject.risks || [];
            const existingThreatTitles = new Set(existingRisks.map(r => r.threat.toLowerCase().trim()));

            // All new risks stay as Pending — the user must Accept or Discard
            const newPendingRisks = candidates
                .filter(nr => !existingThreatTitles.has(nr.threat.toLowerCase().trim()))
                .map(nr => ({
                    ...nr,
                    status: 'Pending' as const,
                    source: 'module_analysis' as const,
                    createdAt: new Date().toISOString(),
                }));

            // Merge: new pending risks float to the top of the array
            const mergedRisks = [...newPendingRisks, ...existingRisks];

            // 3. Overall risk score ONLY counts accepted (non-Pending) risks
            const acceptedRisks = existingRisks.filter(r => r.status !== 'Pending');
            const sortedByScore = [...acceptedRisks].sort((a, b) => b.score - a.score);
            const topRisks = sortedByScore.slice(0, 5);
            const overallRiskScore = topRisks.length > 0
                ? Math.round(topRisks.reduce((acc, r) => acc + r.score, 0) / topRisks.length)
                : selectedProject.overallRiskScore || 0;

            // 4. Update the module's result data and mark Ready
            const updatedModules: SrmModule[] = selectedProject.modules.map(m =>
                m.id === moduleId ? { ...m, resultData, status: 'Ready' as SrmModule['status'] } : m
            );

            // 5. Build contextual alert and audit log entry
            const alertLevel: SrmAlert['level'] = newPendingRisks.some(r => r.score >= 16)
                ? 'Critical'
                : newPendingRisks.length > 0 ? 'Warning' : 'Info';

            const moduleLabel = moduleId
                .replace(/_/g, ' ')
                .replace(/\b\w/g, c => c.toUpperCase());

            const systemAlert: SrmAlert = {
                id: `ALERT-MOD-${Date.now()}`,
                timestamp: new Date().toISOString(),
                level: alertLevel,
                title: `${moduleLabel}: ${newPendingRisks.length} Risk Scenario${newPendingRisks.length !== 1 ? 's' : ''} Detected`,
                message: `Intelligence factorization from ${moduleLabel} identified ${newPendingRisks.length} potential risk scenario${newPendingRisks.length !== 1 ? 's' : ''} awaiting your review in the Risk Register.`,
                source: 'Daily Analysis',
                status: 'Active',
            };

            const auditEntry = {
                id: `AUDIT-MOD-${Date.now()}`,
                timestamp: new Date().toISOString(),
                user: 'SRM Intelligence Engine',
                action: 'Module Intelligence Factorization',
                details: `${moduleLabel} analysis complete. ${newPendingRisks.length} pending risk scenario${newPendingRisks.length !== 1 ? 's' : ''} staged for user review.`,
            };

            const updatedProject = {
                ...selectedProject,
                modules: updatedModules,
                risks: mergedRisks,
                overallRiskScore,
                alerts: newPendingRisks.length > 0
                    ? [systemAlert, ...(selectedProject.alerts || [])]
                    : selectedProject.alerts || [],
                auditLog: [auditEntry, ...(selectedProject.auditLog || [])],
                updatedAt: new Date().toISOString(),
            };

            await setDoc(doc(db, 'srm_projects', projectId), updatedProject);
            setSelectedProject(updatedProject);

            console.log(`✅ Module ${moduleId} saved. ${newPendingRisks.length} pending risk(s) staged for review.`);
        } catch (error) {
            console.error('❌ Error updating SRM module:', error);
            void showAppAlert('Failed to save updated intelligence to project.');
        }
    };


    if (isLoading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader message="Initializing Mission Control..." /></div>;

    return (
        <div className="min-h-screen bg-slate-50 animate-fade-in flex flex-col">
            {/* GLOBAL COMMAND CENTER HEADER — Light Analytical Style */}
            <div className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
                <div className="max-w-[1800px] mx-auto px-6 h-16 flex items-center justify-between">

                    <div className="flex items-center gap-8">
                        <div className={`flex items-center gap-3 ${readOnly ? '' : 'cursor-pointer'}`} onClick={readOnly ? undefined : () => setActiveView('list')}>
                            <div className="p-2 bg-slate-900 rounded-lg shadow-sm">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                </svg>
                            </div>
                            <div>
                                <h1 className="text-lg font-bold text-slate-900 leading-none">{readOnly ? 'SRM Framework Viewer' : 'SRM Command Center'}</h1>
                                <p className="text-[10px] text-slate-400 font-semibold tracking-widest uppercase mt-0.5">{readOnly ? 'Client access' : 'Enterprise Shield'}</p>
                            </div>
                        </div>

                        <div className="h-10 w-px bg-slate-700"></div>

                        {/* Breadcrumbs & Navigation */}
                        <div className="flex items-center gap-4">
                            {!readOnly && activeView !== 'list' && (
                                <button 
                                    onClick={() => setActiveView('list')}
                                    className="flex items-center gap-2 text-xs font-semibold text-slate-500 hover:text-slate-900 transition-colors"
                                >
                                    <ChevronLeftIcon className="w-4 h-4" />
                                    Project List
                                </button>
                            )}
                            
                            {!readOnly && activeView === 'dashboard' && selectedProject && (
                                <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 px-4 py-2 rounded-lg">
                                    <label className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Active Focus</label>
                                    <select 
                                        value={selectedProject?.id}
                                        onChange={(e) => handleSwitchProject(e.target.value)}
                                        className="bg-transparent border-none text-slate-800 font-semibold text-sm focus:ring-0 outline-none cursor-pointer min-w-[200px]"
                                    >
                                        {projects
                                            .filter(p => (p.status as string) !== 'Archived')
                                            .map(p => (
                                            <option key={p.id} value={p.id}>
                                                {p.projectNumber ? `[${p.projectNumber}] ` : ''}{p.name || p.projectName}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {activeView === 'architect' && (
                                <span className="text-xs font-bold text-blue-600 uppercase tracking-[0.2em]">Architecture Protocol Active</span>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center gap-4">
                        {!readOnly && activeView !== 'architect' && (
                            <button 
                                onClick={() => setActiveView('architect')}
                                className="flex items-center gap-2 px-4 py-2 bg-slate-900 hover:bg-slate-700 text-white text-xs font-bold uppercase tracking-widest rounded-lg transition-all shadow-sm"
                            >
                                <PlusIcon className="w-4 h-4" />
                                Launch Auto-Architect
                            </button>
                        )}
                        
                        <div className="h-8 w-px bg-slate-700 mx-2"></div>
                        
                        <button 
                            onClick={() => onNavigate({ page: 'organizationDashboard' })}
                            className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors flex items-center gap-2 text-xs font-semibold"
                            title="Return to ECC"
                        >
                            <Squares2X2Icon className="w-5 h-5" />
                            <span className="hidden lg:inline uppercase tracking-widest">ECC Hub</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* MAIN VIEWPORT */}
            <div className="flex-grow max-w-[1800px] w-full mx-auto p-6 md:p-10">
                {!readOnly && activeView === 'list' && (
                    <SrmProjectsList 
                        projects={projects}
                        organization={organization}
                        onSelectProject={(p) => {
                            setSelectedProject(p);
                            setActiveView('dashboard');
                        }}
                        onBack={() => onNavigate({ page: 'organizationDashboard' })}
                        activeClientId={activeClientId}
                        activeClientName={activeClientName}
                    />
                )}

                {!readOnly && activeView === 'architect' && (
                    <SrmArchitectInput 
                        organization={organization}
                        onGenerate={(p) => {
                            setSelectedProject(p);
                            setActiveView('dashboard');
                        }}
                        onSave={handleSaveSrmProject}
                        onViewProjects={() => setActiveView('list')}
                        onNavigate={onNavigate}
                    />
                )}

                {readOnly && selectedProject && organization && (
                    <SrmProjectDashboard
                        project={selectedProject}
                        organization={organization}
                        onNavigate={onNavigate}
                        onBack={() => onNavigate({ page: 'organizationDashboard' })}
                        navParams={navParams}
                        readOnly
                    />
                )}

                {!readOnly && activeView === 'dashboard' && selectedProject && organization && (
                    <SrmProjectDashboard 
                        project={selectedProject}
                        organization={organization}
                        onNavigate={onNavigate}
                        onBack={() => setActiveView('list')}
                        onUpdateModule={handleUpdateSrmProjectModule}
                        onDeploy={onDeploy}
                        navParams={navParams}
                    />
                )}

                {activeView === 'dashboard' && !selectedProject && (
                    <div className="flex flex-col items-center justify-center h-[60vh] text-center">
                        <div className="w-20 h-20 bg-slate-200 rounded-full mb-4 animate-pulse"></div>
                        <h3 className="text-xl font-bold text-slate-800">Select a mission to begin</h3>
                    </div>
                )}
            </div>

            {/* ── Critical Alert Banner (above status bar) ── */}
            {!readOnly && criticalAlertBanner && !bannerDismissed && (
                <div className="bg-red-600 text-white border-y border-red-700 animate-fade-in">
                    <div className="max-w-[1800px] mx-auto px-6 py-2.5 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="relative flex">
                                <span className="absolute inline-flex h-full w-full rounded-full bg-red-300 opacity-75 animate-ping"></span>
                                <BellAlertIcon className="relative w-4 h-4 text-white" />
                            </div>
                            <span className="text-xs font-black uppercase tracking-widest">
                                ⚠️ {criticalAlertBanner.alertCount} Critical Risk Alert{criticalAlertBanner.alertCount > 1 ? 's' : ''} — project:
                            </span>
                            <span className="text-xs font-bold bg-red-700/60 px-3 py-0.5 rounded-full">
                                {criticalAlertBanner.projectName}
                            </span>
                            <button
                                onClick={() => {
                                    const match = projects.find(p => p.id === criticalAlertBanner.projectId);
                                    if (match) { setSelectedProject(match); setActiveView('dashboard'); }
                                    setBannerDismissed(true);
                                }}
                                className="text-xs font-black bg-white text-red-600 px-4 py-1 rounded-full hover:bg-red-50 transition-colors ml-2"
                            >
                                Review Now →
                            </button>
                        </div>
                        <button
                            onClick={() => setBannerDismissed(true)}
                            className="p-1 hover:bg-red-700 rounded-full transition-colors"
                            title="Dismiss banner"
                        >
                            <XMarkIcon className="w-4 h-4 text-red-200" />
                        </button>
                    </div>
                </div>
            )}

            {/* GLOBAL STATUS BAR */}
            <div className="bg-white border-t border-slate-200 px-6 py-2 flex justify-between items-center text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                <div className="flex items-center gap-4">
                    <span className="flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full shadow-[0_0_5px_rgba(16,185,129,0.5)]"></span>
                        Neural Link Active
                    </span>
                    <span>&bull;</span>
                    <span>Organization: {getOrganizationDisplayName(organization)}</span>
                </div>
                <div className="flex items-center gap-4">
                    <span>Total Frameworks: {projects.length}</span>
                    <span>&bull;</span>
                    <span>System Time: {new Date().toLocaleTimeString()}</span>
                </div>
            </div>
        </div>
    );
};



export default SrmCommandCenter;

